<template>
  <div :class="$style['content']">
    <div :class="$style['filters']">
      <div :class="$style['filters-text']">
        <ArrowTrendingUpIcon :class="$style['filters-text-icon']"/> My Profits
      </div>
      <div v-if="!loading" :class="$style['filters-box']">
        <template v-for="filter in filters" :key="filter.label">
          <span :class="[$style['filters-option'], {[$style['active']]: isCurrentFilter(filter.name)}]" @click="setData(filter.name)">
            {{ filter.label }}
          </span>
        </template>
      </div>
    </div>
    <div :class="$style['box-c']">
      <div v-if="loading && !error" :class="$style['box-c-loading']">
        <LoadingSpinner />
      </div>
      <Line v-if="!loading && !error" ref="line" :data="chartData" :options="options" />
      <div v-if="error" :class="$style['box-c-loading']" style="padding-top: 86px;">
        No Data Found
      </div>
    </div>
  </div>
</template>

<script>

import { Line } from 'vue-chartjs'
import { Chart as ChartJS,CategoryScale, LogarithmicScale, LineElement, PointElement, LinearScale, Title, Tooltip, Legend, TimeScale } from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ArrowTrendingUpIcon } from '@heroicons/vue/24/outline';
import api from '@/axios';
import LoadingSpinner from './LoadingSpinner.vue';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  TimeScale,
  LogarithmicScale
)

export default {
  name: 'LineChart',
  components: { Line, ArrowTrendingUpIcon, LoadingSpinner },

  props: {
  },

  data() {
    return {
      chartData: {
        labels: 'Quotations',
        datasets: [
          {
            label: 'Profit',
            backgroundColor: '#BBF90A',
            borderColor: '#BBF90A',
            borderWidth: 1.5,
            pointRadius: 0,
            data: []
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        responsive:true,
        scales: {
          y: {
            type: "linear",
            position: "left",
            autoSkip: false,
            stacked: true,
            grid: {
              display: true,
              color: "#535353"
            },
            beginAtZero: false,
          },
          x: {
            stacked: true,
            grid: {
              display: true,
              color: "#535353"
            },
            type: 'time',
            time: {
              unit: 'day', // Base da exibição diária
              tooltipFormat: 'dd-MM-yyyy', // Formato no tooltip
              displayFormats: {
                day: 'dd-MM',
                month: 'MMM-yyyy', // Mostra o mês-ano apenas no início
              },
            },
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                let value = context.raw;

                return `Profit ${value}%`;
              }
            }
          }
        },
      },
      filters: [
        {
          name: '3months',
          label: '3M'
        },
        {
          name: '6months',
          label: '6M'
        },
        {
          name: '1year',
          label: '1Y'
        },
        {
          name: '2years',
          label: '2Y'
        },
      ],
      auxFiltredData: [],
      currentFilter: '3months',
      profits: [],
      loading: true,
      error: false
    }
  },

  async mounted() {

    await this.getData()

    this.setData(this.currentFilter);
  },

  methods: {
    async getData() {
      try {
        const token = this.$store.state.auth.token
        
        const response = await api.get('/quotations/', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        this.profits = response.data

        this.loading = false

      } catch (error) {
        this.error = true
      }
    },
    setData(filterName) {
      let selectedData = {};

      // Verifica se os dados do filtro já foram carregados
      if (!this.auxFiltredData[filterName]) {
        // Gera os dados filtrados e os armazena no cache
        selectedData = this.formatDataByDate(filterName);
        this.auxFiltredData[filterName] = selectedData;
      } else {
        // Se já existir no cache, usa os dados existentes
        selectedData = this.auxFiltredData[filterName];
      }

      // Atualiza o filtro atual e os dados filtrados
      this.currentFilter = filterName;
      this.filteredData = selectedData;

      // Atualiza os dados do grafico
      this.chartData.labels = selectedData.labels;
      this.chartData.datasets[0].data = selectedData.data;


      this.$nextTick(() => {
        if (this.$refs.line?.chart) {

          const chart = this.$refs.line.chart

          chart.data.datasets[0].data = selectedData.data
          chart.data.labels = selectedData.labels

          const tickInterval =
            filterName === "3months"
              ? 10 // A cada 10 dias
              : filterName === "6months"
              ? 15 // A cada 15 dias
              : filterName === "1year" || filterName === "2years"
              ? 31 // Mensal
              : 1; // Padrão: exibir todos os dias

          // Atualiza o callback dos ticks no eixo X
          chart.options.scales.x.ticks.callback = (value, index, ticks) => {
            // Exibe o rótulo somente nos índices múltiplos do intervalo
            if (index % tickInterval === 0) {
              const date = new Date(ticks[index].value);


              if (filterName === "3months" || filterName === "6months") {
                // Formata como MM/DD
                return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
                  .getDate()
                  .toString()
                  .padStart(2, "0")}`;
              } else if (filterName === "1year" || filterName === "2years") {
                // Formata como MMM YYYY
                return date.toLocaleString("en-US", {
                  month: "short",
                  year: "numeric",
                });
              }
            }
            return null; // Oculta o rótulo
          };

          // Atualiza os ticks do eixo Y (opcional: para formatação de moeda)
          chart.options.scales.y.ticks.callback = (value) => {
            value = Math.floor(value * 100) / 100;
            return `${value}%`;
          };

          chart.options.scales.y = {
            type: "linear", // Define a escala como logarítmica
            position: "left",
            min: selectedData.minY, // Valor mínimo no eixo
            max: selectedData.maxY, // Valor máximo no eixo
            autoSkip: false,
            ticks: {
              autoSkip: true, // Não pula valores automaticamente
              callback: (value) => {
                // Formata os valores do eixo Y
                value = Math.floor(value * 100) / 100;
                return `${value}%`;
              },
            },
          };

          chart.update()
        }
      });

    },
    isCurrentFilter(filterName) {
      return this.currentFilter === filterName
    },

    formatDataByDate(range) {
      const now = new Date();
      let dateLimit;

      // Define o limite de data com base no intervalo fornecido
      switch (range) {
        case "3months":
          dateLimit = new Date();
          dateLimit.setMonth(now.getMonth() - 1);
          break;

        case "6months":
          dateLimit = new Date();
          dateLimit.setMonth(now.getMonth() - 6);
          break;

        case "1year":
          dateLimit = new Date();
          dateLimit.setFullYear(now.getFullYear() - 1);
          break;

        case "2years":
          dateLimit = new Date();
          dateLimit.setFullYear(now.getFullYear() - 2);
          break;

        default:
          // default 3m
          dateLimit = new Date();
          dateLimit.setMonth(now.getMonth() - 3);
      }

      // Filtra os dados baseando-se na data limite
      const filteredData = this.profits.filter(item => new Date(item.date) >= dateLimit);

      const aux = filteredData.map((item) => item.quotation);

      // Retorna os dados e rótulos formatados
      return {
        data: filteredData.map(item => {const value = Math.floor(item.quotation * 100) / 100; return Math.trunc(value * 100) / 100 }),
        labels: filteredData.map(item => item.date),
        minY:  (Math.floor(Math.min(...aux) * 100) / 100 - 0.05),
        maxY:  (Math.floor(Math.max(...aux) * 100) / 100 + 0.05)
      };
    }

  }
}
</script>

<style lang="scss" module>

  .box-c {
    width: 100%;
    position: relative;
    height: 200px;

    @include respond-to(null, 'xs') {
      flex: 1 !important;
      width: unset !important;
    }
    
    &-loading {
      display: flex;
      justify-content: center;
      padding-top: 60px;
    }
  }

  .content {
    border-radius: 16px;
    background: rgba(45, 45, 45, 0.35);
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.10);
    padding: 15px;
    flex: 1 1 70%;
    max-width: 100%;

    @include respond-to('xs', 'md') {
      flex: 1;
      max-width: unset;
    }

    @include respond-to(null, 'xs') {
      max-width: 90%;
    }
  }

  .filters {
    display: flex;
    justify-content: space-between;

    &-text {
      color: #C1C1C1;
      font-family: $font-default;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 26px;
      display: flex;
      align-items: center;

      &-icon {
        width: 16px;
        color: #BBF90A;
        margin-right: 8px;
      }
    }

    &-box {
      border-radius: 8px;
      background: #0B0C0C;
      width: fit-content;
      display: flex;
      gap: 5px;
      padding: 5px 8px;
      height: fit-content;
    }

    &-option {
      color: #D9D9D9;
      font-family: $font-default;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      cursor: pointer;
      padding: 2px 4px;

      &.active,
      &:hover {
        border-radius: 4px;
        background: rgba(45, 45, 45, 0.35);
      }
    }
  }
</style>